import React, { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "../../lib/animations";

const SEGMENTS = [
  {
    id: "solar",
    title: "Solar Energy Companies",
    accent: "gold",
    imageSrc: "/Recovering_R_4/Solar_company.png",
  },
  {
  id: "property",
  title: "Property Management ",
  accent: "purple",
  imageSrc: "/Recovering_R_4/Elite_%20Pay.png",
},
  {
    id: "gyms",
    title: "Gym & Fitness",
    accent: "gold",
    imageSrc: "/Recovering_R_4/gym%20for%20Crown_ledger.png",
  },
  {
    id: "fibre",
    title: "Fibre & ISP",
    accent: "purple",
    imageSrc: "/Recovering_R_4/fibre_for%20crown_ledger.png",
  },
  {
    id: "healthcare",
    title: "Healthcare",
    accent: "gold",
    imageSrc: "/Recovering_R_4/Dental%20Practise_crown_ledger.png",
  },
  {
    id: "saas",
    title: "SaaS",
    accent: "purple",
    imageSrc: "/Recovering_R_4/Digital%20Education_%20System.png",
  },
  {
    id: "ecommerce",
    title: "E-commerce",
    accent: "gold",
    imageSrc: "/Recovering_R_4/Ecommerce%20%26%20Subscription_%20Crown_ledger.png",
  },

];

function Card({ segment }) {
  const isGold = segment.accent === "gold";
  return (
    <motion.article
      variants={fadeUp}
      className="relative shrink-0 snap-start overflow-hidden"
      style={{
        width: "clamp(210px, 70vw, 300px)",
        aspectRatio: "4 / 3",
        borderRadius: 24,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.10)",
        boxShadow:
          "0 22px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)",
      }}
      data-testid={`built-for-card-${segment.id}`}
      whileHover={{ y: -3, scale: 1.01 }}
    >
      {segment.imageSrc ? (
        <img
          src={segment.imageSrc}
          alt={segment.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isGold
            ? "radial-gradient(ellipse 70% 60% at 30% 20%, rgba(232,200,120,0.26), transparent 58%)"
            : "radial-gradient(ellipse 70% 60% at 30% 20%, rgba(183,148,244,0.26), transparent 58%)",
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(10,8,15,0.10) 0%, rgba(10,8,15,0.30) 40%, rgba(10,8,15,0.78) 100%)",
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 320,
          height: 320,
          right: -80,
          bottom: -120,
          background: isGold
            ? "radial-gradient(circle, rgba(232, 200, 120, 0.32) 0%, rgba(184, 146, 42, 0.12) 30%, transparent 60%)"
            : "radial-gradient(circle, rgba(183, 148, 244, 0.32) 0%, rgba(102, 54, 157, 0.12) 30%, transparent 60%)",
          filter: "blur(10px)",
        }}
      />

      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          padding: "14px 16px",
          background: "linear-gradient(180deg, rgba(10,8,15,0.00), rgba(10,8,15,0.72))",
          backdropFilter: "blur(10px) saturate(160%)",
          WebkitBackdropFilter: "blur(10px) saturate(160%)",
        }}
      >
        <h3
          className="font-display font-semibold tracking-tight text-white-pure"
          style={{
            fontSize: "clamp(15px, 1.6vw, 18px)",
            lineHeight: 1.15,
            textShadow: "0 10px 24px rgba(0,0,0,0.70)",
          }}
        >
          {segment.title}
        </h3>
      </div>
    </motion.article>
  );
}

export default function BuiltFor() {
  const scrollRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.55;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section
      id="built-for"
      className="bg-lift-purple relative section-noise overflow-hidden"
      style={{
        paddingTop: "clamp(64px, 7.5vw, 120px)",
        paddingBottom: "clamp(64px, 7.5vw, 120px)",
      }}
      data-testid="built-for-section"
    >
      <div className="relative z-10 max-w-[1280px] mx-auto px-6">
        {/* Heading row */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-9 md:mb-10"
        >
          <div className="max-w-[640px]">
            <h2
              className="font-syne font-semibold uppercase tracking-[0.22em] text-pri"
              style={{ fontSize: "clamp(18px, 2.2vw, 24px)", lineHeight: 1.1 }}
            >
              RECOVERING REVENUE FOR:
            </h2>
          </div>

          {/* Arrow controls */}
          <motion.div
            variants={fadeUp}
            className="hidden md:flex items-center gap-2"
          >
            <button
              onClick={() => scrollBy(-1)}
              aria-label="Previous"
              data-testid="built-for-prev"
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: "rgba(184, 146, 42, 0.04)",
                border: "1px solid rgba(184, 146, 42, 0.20)",
                color: "var(--text-secondary)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--gold-primary)";
                e.currentTarget.style.color = "var(--gold-bright)";
                e.currentTarget.style.background = "rgba(184,146,42,0.10)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(184, 146, 42, 0.20)";
                e.currentTarget.style.color = "var(--text-secondary)";
                e.currentTarget.style.background = "rgba(184, 146, 42, 0.04)";
              }}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              aria-label="Next"
              data-testid="built-for-next"
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: "rgba(184, 146, 42, 0.04)",
                border: "1px solid rgba(184, 146, 42, 0.20)",
                color: "var(--text-secondary)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--gold-primary)";
                e.currentTarget.style.color = "var(--gold-bright)";
                e.currentTarget.style.background = "rgba(184,146,42,0.10)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(184, 146, 42, 0.20)";
                e.currentTarget.style.color = "var(--text-secondary)";
                e.currentTarget.style.background = "rgba(184, 146, 42, 0.04)";
              }}
            >
              <ChevronRight size={18} />
            </button>
          </motion.div>
        </motion.div>

        {/* Scrollable cards strip */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 no-scrollbar"
          style={{
            scrollPaddingLeft: 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {SEGMENTS.map((seg, i) => (
            <Card key={seg.id} segment={seg} />
          ))}
          {/* trailing spacer to give breathing room when scrolled fully right */}
          <div className="shrink-0" style={{ width: 24 }} />
        </motion.div>
      </div>
    </section>
  );
}
